// import React from "react";
import SEO from "@/components/SEO";
import { seoConfig } from "@/config/seo.config";
import { Check, Star } from "lucide-react";

const plans = [
  {
    name: "Starter AI",
    description: "Get Started with Starter AI today. Perfect for individuals.",
    monthlyPrice: 29.99,
    yearlyPrice: 299.99,
    features: [
      "1 AI Chatbot Agent",
      "1 Human Agent",
      "5000 AI Messages",
      "5000 Human Messages",
    ],
    oneTimeLimits: ["100 Training File Pages", "20 Website Pages"],
  },
  {
    name: "Growth AI",
    description: "Get Started with Growth AI. Perfect for small teams.",
    monthlyPrice: 49.99,
    yearlyPrice: 499.99,
    features: [
      "5 AI Chatbot Agents",
      "5 Human Agents",
      "25000 AI Messages",
      "Unlimited Human Messages",
    ],
    oneTimeLimits: ["500 Training File Pages", "100 Website Pages"],
  },
  {
    name: "Enterprise AI",
    description:
      "Get Started with Enterprise AI. Perfect for high volume teams.",
    monthlyPrice: 99.99,
    yearlyPrice: 999.99,
    features: [
      "20 AI Chatbot Agents",
      "20 Human Agents",
      "100000 AI Messages",
      "Unlimited Human Messages",
    ],
    oneTimeLimits: ["1000 Training File Pages", "500 Website Pages"],
  },
];

interface PricingPlansProps {
  selectedFrequency: string;
  isPageLoading: boolean;
  handlePlanClick: (plan: string) => Promise<void>;
}

export default function PricingPlans({
  selectedFrequency,
  isPageLoading,
  handlePlanClick,
}: PricingPlansProps) {
  return (
    <>
      {plans.map((plan, index) => (
        <div
          key={index}
          className="flex flex-col rounded-3xl bg-white shadow-xl ring-1 ring-black/10"
        >
          <div className="p-8 sm:p-10 sm:pb-2">
            <div className="flex">
              <h3
                className="text-lg font-semibold leading-8 tracking-tight text-[#000]"
                id={`tier-${plan.name.toLowerCase().replace(" ", "-")}`}
              >
                {plan.name}
              </h3>
            </div>
            <div className="mt-4 flex items-baseline text-4xl tracking-tight text-gray-900 font-semibold">
              $
              {selectedFrequency === "yearly"
                ? plan.yearlyPrice
                : plan.monthlyPrice}
              <span className="text-lg font-semibold leading-8 tracking-normal text-gray-500 dark:text-gray-400">
                {selectedFrequency === "yearly" ? "/year" : "/month"}
              </span>
            </div>
            <p className="mt-3 text- leading-7 text-gray-600">
              {plan.description}
            </p>
          </div>
          <div className="flex flex-1 flex-col p-2">
            <div className="flex flex-1 flex-col justify-between rounded-2xl bg-gray-50 p-6 sm:p-8 sm:pt-2">
              <ul role="list" className="space-y-4">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start">
                    <div className="flex-shrink-0">
                      <Check className="h-6 w-6 text-[#000]" />
                    </div>
                    <p className="ml-3 text-sm leading-6 text-gray-600">
                      {feature}
                    </p>
                  </li>
                ))}

                <hr className="my-4 border-gray-200" />

                {plan.oneTimeLimits.map((oneTimeLimits, oneTimeLimitsIndex) => (
                  <li key={oneTimeLimitsIndex} className="flex items-start">
                    <div className="flex-shrink-0">
                      <Star className="h-6 w-6 text-[#000]" />
                    </div>
                    <p className="ml-3 text-sm leading-6 text-gray-600">
                      {oneTimeLimits}
                    </p>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <div
                  className={`inline-block w-full rounded-lg bg-[#06526d] px-4 py-2.5 text-center text-sm font-semibold leading-5 border transition text-white shadow-md hover:bg-[#fff] hover:text-[#06526d] hover:border-[#06526d] cursor-pointer ${
                    isPageLoading
                      ? "opacity-50 cursor-not-allowed bg-gray-300 text-gray-500 border-gray-300 hover:text-gray-500"
                      : ""
                  }`}
                  aria-describedby={`tier-${plan.name
                    .toLowerCase()
                    .replace(" ", "-")}`}
                  onClick={() => {
                    handlePlanClick(plan.name);
                  }}
                >
                  {isPageLoading ? (
                    <div>
                      <div className="spinner-border text-dark" role="status">
                        <span className="+">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    "Choose Plan"
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}
